import { QuestionCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { round } from '@mahawi/eshop-common/src/round';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { stringProbableMatch } from '@mahawi/eshop-common/src/string-probable-match';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  EPairingMatchType,
  type ICurrencyType,
  type IReportWarehouseProductsPairing,
  type IWarehouseDetail,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  Divider,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { BaseType } from 'antd/es/typography/Base';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import StatusIcon from '../components/status-icon';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { renderProductCode } from '../fragments/table-product-code';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  downloaderSyncWarehouseProduct,
  downloaderWarehouseProductPairWithExistingProduct,
} from '../reducers/downloader/reducer';
import { reportWarehouseProductsPairingLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends IReportWarehouseProductsPairing {
  key: string;
  isMpnSame: boolean;
  isBrandSame: boolean;
  isBrandCheckable: boolean;
  isGtinSame: boolean;
  nameProbableMatch: number;
  productLocaleName: string;
  currencyType?: ICurrencyType;
  pairingScore: number;
}

const MATCH_TYPE_TAG_COLOR: Record<EPairingMatchType, string> = {
  [EPairingMatchType.GTIN]: 'green',
  [EPairingMatchType.MPN]: 'blue',
  [EPairingMatchType.SEMANTIC]: 'purple',
};

const MATCH_TYPE_LABEL: Record<EPairingMatchType, string> = {
  [EPairingMatchType.GTIN]: 'GTIN',
  [EPairingMatchType.MPN]: 'MPN',
  [EPairingMatchType.SEMANTIC]: 'AI',
};

function computePairingScore({
  isGtinSame,
  isMpnSame,
  isBrandSame,
  isBrandCheckable,
  nameProbableMatch,
}: {
  isGtinSame: boolean;
  isMpnSame: boolean;
  isBrandSame: boolean;
  isBrandCheckable: boolean;
  nameProbableMatch: number;
}): number {
  if (isGtinSame) return 100;
  let score = 0;
  if (isMpnSame) score += 50;
  if (isBrandCheckable && isBrandSame) score += 20;
  score += Math.round(nameProbableMatch * 30);
  return Math.min(100, score);
}

function ReportWarehouseProductsPairingContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportWarehouseProductsPairing = useSelector(
    (s: RootState) => s.Report.warehouseProductsPairing,
  );
  const reportUpdatedAt = useSelector((s: RootState) => s.Report.updatedAt);
  const reportInProcess = useSelector((s: RootState) => s.Report.inProcess);
  const warehouseWarehouses = useSelector(
    (s: RootState) => s.Warehouse.warehouses,
  );
  const warehouseUpdatedAt = useSelector(
    (s: RootState) => s.Warehouse.updatedAt,
  );
  const languageLanguageType = useSelector(
    (s: RootState) => s.Language.languageType,
  );

  const { token } = theme.useToken();
  const [warehouseOptions, setWarehouseOptions] = useState<DefaultOptionType[]>(
    [],
  );
  const [warehouseUuid, setWarehouseUuid] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [enabledBrandCheck, setenabledBrandCheck] = useState<boolean>(true);
  const [nameProbableMatchThreshold, setNameProbableMatchThreshold] =
    useState<number>(0.25);
  const [excludeStaleProducts, setExcludeStaleProducts] =
    useState<boolean>(true);
  const [includeSemanticMatches, setIncludeSemanticMatches] =
    useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [pairedCount, setPairedCount] = useState<number>(0);
  const [filteredDataSource, setFilteredDataSource] = useState<DataType[]>([]);

  const productIsPairable = useCallback(
    (record: DataType): boolean => {
      if (record.matchType === EPairingMatchType.SEMANTIC) return false;
      if (record.isGtinSame) return true;
      if (!record.isMpnSame) return false;
      if (record.nameProbableMatch < nameProbableMatchThreshold) return false;
      if (enabledBrandCheck && record.isBrandCheckable && !record.isBrandSame) {
        return false;
      }
      return true;
    },
    [enabledBrandCheck, nameProbableMatchThreshold],
  );

  useEffect((): void => {
    const warehouseOptionsUE: DefaultOptionType[] =
      warehouseWarehouses
        ?.filter((warehouse: IWarehouseDetail): boolean => warehouse.isActive)
        .map((warehouse: IWarehouseDetail): DefaultOptionType => ({
          label: warehouse.name,
          value: warehouse.uuid,
        })) || [];

    setWarehouseOptions(warehouseOptionsUE);
  }, [warehouseWarehouses, warehouseUpdatedAt]);

  const removeRows = useCallback((keys: Key[]): void => {
    const keySet = new Set(keys);
    setDataSource((prev) => prev.filter((row) => !keySet.has(row.key)));
    setFilteredDataSource((prev) => prev.filter((row) => !keySet.has(row.key)));
    setPairedCount((c) => c + keys.length);
    setSelectedRowKeys((prev) => prev.filter((k) => !keySet.has(k)));
  }, []);

  useEffect((): void => {
    setPairedCount(0);

    const ct: ICurrencyType | undefined = warehouseWarehouses?.find(
      (warehouse: IWarehouseDetail): boolean =>
        warehouse.uuid === warehouseUuid,
    )?.currencyType;

    const dataSourceUE: DataType[] =
      reportWarehouseProductsPairing?.map(
        (warehouseProduct: IReportWarehouseProductsPairing): DataType => {
          const productLocaleName: string | null = getTranslation(
            warehouseProduct.product.names,
            languageLanguageType,
          );

          // Use best name match across all available translations
          const nameProbableMatch: number = Math.max(
            0,
            ...warehouseProduct.product.names.map((t) =>
              stringProbableMatch({
                first: warehouseProduct.warehouseProduct.name,
                second: t.value,
              }),
            ),
          );

          const isMpnSame: boolean =
            warehouseProduct.warehouseProduct.mpn?.toLocaleLowerCase() ===
            warehouseProduct.product.mpn?.toLocaleLowerCase();

          const wpGtin = warehouseProduct.warehouseProduct.gtin;
          const pGtin = warehouseProduct.product.gtin;
          const isGtinSame: boolean =
            wpGtin !== null &&
            pGtin !== null &&
            wpGtin.toLocaleLowerCase() === pGtin.toLocaleLowerCase();

          const isBrandCheckable: boolean =
            warehouseProduct.warehouseProduct.brand.length > 0 &&
            warehouseProduct.product.brand !== null &&
            warehouseProduct.product.brand.length > 0;

          const isBrandSame: boolean =
            isBrandCheckable &&
            warehouseProduct.warehouseProduct.brand.toLocaleLowerCase() ===
              warehouseProduct.product.brand!.toLocaleLowerCase();

          const pairingScore = Math.max(
            computePairingScore({
              isGtinSame,
              isMpnSame,
              isBrandSame,
              isBrandCheckable,
              nameProbableMatch,
            }),
            Math.round((warehouseProduct.similarity ?? 0) * 100),
          );

          return {
            key: warehouseProduct.warehouseProduct.uuid,
            ...warehouseProduct,
            isMpnSame,
            isBrandSame,
            isBrandCheckable,
            isGtinSame,
            nameProbableMatch,
            productLocaleName: productLocaleName || EMPTY_VALUE,
            currencyType: ct,
            pairingScore,
          };
        },
      ) || [];

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Match',
        dataIndex: 'matchType',
        filters: Object.values(EPairingMatchType).map(
          (matchType: EPairingMatchType) => ({
            text: MATCH_TYPE_LABEL[matchType],
            value: matchType,
          }),
        ),
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.matchType === value,
        render: (
          matchType: EPairingMatchType,
          record: DataType,
        ): React.ReactElement => (
          <Tooltip
            title={
              record.similarity === null
                ? undefined
                : `Similarity ${round(record.similarity * 100, 0)} %`
            }
          >
            <Tag color={MATCH_TYPE_TAG_COLOR[matchType]}>
              {MATCH_TYPE_LABEL[matchType]}
            </Tag>
          </Tooltip>
        ),
        width: '4%',
      },
      {
        title: 'Score',
        dataIndex: 'pairingScore',
        sorter: (a: DataType, b: DataType): number =>
          a.pairingScore - b.pairingScore,
        defaultSortOrder: 'descend',
        filterSearch: true,
        filters: [
          { text: 'Product is pairable', value: 'productIsPairable' },
          { text: 'Product is not pairable', value: 'productIsNotPairable' },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'productIsPairable') return productIsPairable(record);
          if (value === 'productIsNotPairable')
            return !productIsPairable(record);
          return false;
        },
        render: (score: number): React.ReactElement => {
          let color = 'red';
          if (score >= 80) color = 'green';
          else if (score >= 50) color = 'orange';
          return <Tag color={color}>{score}%</Tag>;
        },
        width: '5%',
      },
      {
        title: 'Name',
        dataIndex: ['warehouseProduct', 'name'],
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouseProduct.name, b.warehouseProduct.name),
        filterSearch: true,
        filters: dataSourceUE
          .map((data: DataType) => ({
            text: data.warehouseProduct.name,
            value: data.warehouseProduct.name,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.warehouseProduct.name.indexOf(value) === 0,
        render: (name: string, record: DataType): React.ReactElement => {
          return (
            <Space size={SPACING.sm} align="center" orientation="horizontal">
              <Typography.Text
                strong
                type={
                  record.nameProbableMatch >= nameProbableMatchThreshold
                    ? 'success'
                    : 'danger'
                }
              >
                {round(record.nameProbableMatch * 100, 2)}%
              </Typography.Text>

              <Space size={SPACING.sm} align="start" orientation="vertical">
                <Link
                  to={`/downloader/warehouse-product/${record.warehouseProduct.uuid}`}
                  label={name}
                  openInNewTab
                />

                <Link
                  to={`/product/${record.product.uuid}`}
                  label={record.productLocaleName}
                  openInNewTab
                />
              </Space>
            </Space>
          );
        },
        width: '30%',
      },
      {
        title: 'MPN',
        dataIndex: ['warehouseProduct', 'mpn'],
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouseProduct.mpn, b.warehouseProduct.mpn),
        filterSearch: true,
        filters: dataSourceUE
          .map((data: DataType) => ({
            text: data.warehouseProduct.mpn || EMPTY_VALUE,
            value: data.warehouseProduct.mpn || EMPTY_VALUE,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean => {
          return record.warehouseProduct.mpn?.indexOf(value) === 0;
        },
        render: (mpn: string, record: DataType): React.ReactElement => {
          const productMpn: string | null = record.product.mpn;

          return (
            <Space size={SPACING.sm} align="center" orientation="horizontal">
              <Typography.Text
                strong
                type={record.isMpnSame ? 'success' : 'danger'}
              >
                <StatusIcon ok={record.isMpnSame} />
              </Typography.Text>

              <Space size={SPACING.sm} align="start" orientation="vertical">
                {mpn}

                {productMpn}
              </Space>
            </Space>
          );
        },
        width: '10%',
      },
      {
        title: 'GTIN',
        dataIndex: ['warehouseProduct', 'gtin'],
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouseProduct.gtin, b.warehouseProduct.gtin),
        render: (
          _gtin: string | null,
          record: DataType,
        ): React.ReactElement => {
          const wpGtin = record.warehouseProduct.gtin;
          const pGtin = record.product.gtin;
          const bothPresent = wpGtin !== null && pGtin !== null;

          return (
            <Space size={SPACING.sm} align="center" orientation="horizontal">
              {bothPresent ? (
                <Typography.Text
                  strong
                  type={record.isGtinSame ? 'success' : 'danger'}
                >
                  <StatusIcon ok={record.isGtinSame} />
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">
                  {EMPTY_VALUE}
                </Typography.Text>
              )}
              <Space size={SPACING.xs} align="start" orientation="vertical">
                <Typography.Text>{wpGtin || EMPTY_VALUE}</Typography.Text>
                <Typography.Text>{pGtin || EMPTY_VALUE}</Typography.Text>
              </Space>
            </Space>
          );
        },
        width: '10%',
      },
      {
        title: 'Product code',
        dataIndex: ['warehouseProduct', 'productCode'],
        render: (productCode: string, record: DataType): React.ReactElement =>
          renderProductCode(productCode, record.warehouseProduct.url),
        width: '10%',
      },
      {
        title: 'Brand',
        dataIndex: ['warehouseProduct', 'brand'],
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.warehouseProduct.brand, b.warehouseProduct.brand),
        filterSearch: true,
        filters: dataSourceUE
          .map((data: DataType) => ({
            text: data.warehouseProduct.brand,
            value: data.warehouseProduct.brand,
          }))
          .sort((a, b) => stringCompare(a.text, b.text)),
        onFilter: (value: string, record: DataType): boolean =>
          record.warehouseProduct.brand.indexOf(value) === 0,
        render: (brand: string, record: DataType): React.ReactElement => {
          let type: BaseType = record.isBrandSame ? 'success' : 'danger';
          let checkIcon: React.ReactElement = (
            <StatusIcon ok={record.isBrandSame} />
          );

          if (!record.isBrandCheckable) {
            type = 'secondary';
            checkIcon = (
              <QuestionCircleOutlined
                style={{ color: token.colorTextDisabled }}
              />
            );
          } else if (!enabledBrandCheck && !record.isBrandSame) {
            type = 'warning';
            checkIcon = (
              <QuestionCircleOutlined style={{ color: token.colorWarning }} />
            );
          }

          return (
            <Space size={SPACING.sm} align="center" orientation="horizontal">
              <Typography.Text strong type={type}>
                {checkIcon}
              </Typography.Text>
              <Space size={SPACING.sm} align="start" orientation="vertical">
                {brand}

                {record.product.brand}
              </Space>
            </Space>
          );
        },
        width: '5%',
      },
      {
        title: 'Product is active',
        dataIndex: ['product', 'isActive'],
        sorter: (a: DataType, b: DataType): number =>
          Number(a.product.isActive) - Number(b.product.isActive),
        filterSearch: true,
        filters: [
          {
            text: 'Active',
            value: 'active',
          },
          {
            text: 'Inactive',
            value: 'inactive',
          },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'active') {
            return record.product.isActive;
          }

          if (value === 'inactive') {
            return !record.product.isActive;
          }

          return false;
        },
        render: (isActive: boolean): React.ReactElement => (
          <StatusIcon ok={isActive} />
        ),
        width: '5%',
      },
      {
        title: 'Warehouse product count',
        dataIndex: ['warehouseProduct', 'count'],
        sorter: (a: DataType, b: DataType): number =>
          a.warehouseProduct.count - b.warehouseProduct.count,
        filterSearch: true,
        filters: [
          {
            text: 'In stock',
            value: 'inStock',
          },
          {
            text: 'Full stock',
            value: 'fullStock',
          },
          {
            text: 'Low stock',
            value: 'lowStock',
          },
          {
            text: 'Out of stock',
            value: 'outOfStock',
          },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'inStock') {
            return record.warehouseProduct.count > 0;
          }

          if (value === 'fullStock') {
            return record.warehouseProduct.count >= 5;
          }

          if (value === 'lowStock') {
            return (
              record.warehouseProduct.count > 0 &&
              record.warehouseProduct.count < 5
            );
          }

          if (value === 'outOfStock') {
            return record.warehouseProduct.count === 0;
          }

          return false;
        },
        render: (value: number): React.ReactElement => {
          if (value >= 5) {
            return (
              <Typography.Text strong type="success">
                {value}
              </Typography.Text>
            );
          }
          if (value >= 2) {
            return (
              <Typography.Text strong type="warning">
                {value}
              </Typography.Text>
            );
          }

          return (
            <Typography.Text strong type="danger">
              {value}
            </Typography.Text>
          );
        },
        width: '5%',
      },
      {
        title: 'Warehouse product price',
        dataIndex: ['warehouseProduct', 'price'],
        sorter: (a: DataType, b: DataType): number =>
          a.warehouseProduct.price - b.warehouseProduct.price,
        render: (value: number, record: DataType): React.ReactElement => {
          return (
            <Typography.Text strong>
              {formatCurrency(value, record.currencyType)}
            </Typography.Text>
          );
        },
        width: '5%',
      },
      {
        title: 'Actions',
        render(record: DataType): React.ReactElement {
          const isPairable: boolean = productIsPairable(record);
          if (isPairable) {
            return (
              <Button
                type="primary"
                onClick={(): void => {
                  dispatch(
                    downloaderWarehouseProductPairWithExistingProduct(
                      record.product.uuid,
                      record.warehouseProduct.uuid,
                    ),
                  );
                  removeRows([record.key]);
                }}
              >
                Pair with product
              </Button>
            );
          }

          return (
            <Popconfirm
              placement="bottom"
              title="Are you sure to pair this product?"
              onConfirm={(): void => {
                dispatch(
                  downloaderWarehouseProductPairWithExistingProduct(
                    record.product.uuid,
                    record.warehouseProduct.uuid,
                  ),
                );
                dispatch(
                  downloaderSyncWarehouseProduct(
                    record.product.uuid,
                    record.warehouseProduct.uuid,
                  ),
                );
                removeRows([record.key]);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger>
                Pair with product
              </Button>
            </Popconfirm>
          );
        },
        width: '10%',
      },
    ];

    setDataSource(dataSourceUE);
    setFilteredDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [
    languageLanguageType,
    reportWarehouseProductsPairing,
    reportUpdatedAt,
    warehouseWarehouses,
    warehouseUuid,
    enabledBrandCheck,
    nameProbableMatchThreshold,
  ]);

  const selectedPairableRows = useMemo(
    (): DataType[] =>
      dataSource.filter(
        (row) => selectedRowKeys.includes(row.key) && productIsPairable(row),
      ),
    [dataSource, selectedRowKeys, productIsPairable],
  );

  const pairableCount = useMemo(
    (): number => dataSource.filter((row) => productIsPairable(row)).length,
    [dataSource, productIsPairable],
  );

  const filteredPairableCount = useMemo(
    (): number =>
      filteredDataSource.filter((row) => productIsPairable(row)).length,
    [filteredDataSource, productIsPairable],
  );

  return (
    <>
      <DetailPageHeader
        title="Report warehouse products pairing"
        onReload={(): void => {
          if (warehouseUuid && languageLanguageType) {
            dispatch(
              reportWarehouseProductsPairingLoad(
                warehouseUuid,
                excludeStaleProducts,
                includeSemanticMatches,
              ),
            );
          }
        }}
        isReloadDisabled={warehouseUuid === null || reportInProcess}
        isReloadHighlighted={!reportWarehouseProductsPairing}
      />

      <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
        <Space
          size={SPACING.lg}
          align="center"
          separator={<Divider orientation="vertical" />}
        >
          <Select
            style={{ width: 200 }}
            placeholder="Select warehouse"
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            options={warehouseOptions}
            onChange={(value: string): void => {
              setWarehouseUuid(value);
            }}
          />

          <UpdatedAt value={reportUpdatedAt} />

          <Switch
            checkedChildren="Brand check enabled"
            unCheckedChildren="Brand check disabled"
            checked={enabledBrandCheck}
            onChange={(checked: boolean): void => {
              setenabledBrandCheck(checked);
            }}
          />

          <Tooltip title="Hide warehouse products whose last warehouse sync is more than 1 day old — their stock and price are likely stale and no longer accurate">
            <Switch
              checkedChildren="Recently synced only"
              unCheckedChildren="Include stale"
              checked={excludeStaleProducts}
              onChange={setExcludeStaleProducts}
            />
          </Tooltip>

          <Tooltip title="Also suggest existing products found by AI similarity for warehouse products without a GTIN or MPN match. AI matches are never paired in bulk.">
            <Switch
              checkedChildren="Include AI matches"
              unCheckedChildren="GTIN / MPN only"
              checked={includeSemanticMatches}
              onChange={setIncludeSemanticMatches}
            />
          </Tooltip>

          <Typography>
            <Space size={SPACING.sm} align="center">
              Name probable match threshold:
              <Select
                style={{ width: 100 }}
                defaultValue={nameProbableMatchThreshold}
                onChange={(value: number): void => {
                  setNameProbableMatchThreshold(value);
                }}
                options={[
                  { label: 'off', value: 0 },
                  { label: '5 %', value: 0.05 },
                  { label: '10 %', value: 0.1 },
                  { label: '15 %', value: 0.15 },
                  { label: '25 %', value: 0.25 },
                  { label: '50 %', value: 0.5 },
                  { label: '75 %', value: 0.75 },
                ]}
              />
            </Space>
          </Typography>

          {filteredPairableCount > 0 && (
            <Button
              onClick={(): void => {
                setSelectedRowKeys(
                  filteredDataSource
                    .filter((row) => productIsPairable(row))
                    .map((row) => row.key),
                );
              }}
            >
              Select all pairable ({filteredPairableCount})
            </Button>
          )}

          {selectedPairableRows.length > 0 && (
            <Popconfirm
              placement="bottom"
              title={`Pair ${selectedPairableRows.length} selected product(s)?`}
              onConfirm={(): void => {
                selectedPairableRows.forEach((row) => {
                  dispatch(
                    downloaderWarehouseProductPairWithExistingProduct(
                      row.product.uuid,
                      row.warehouseProduct.uuid,
                    ),
                  );
                });
                removeRows(selectedPairableRows.map((row) => row.key));
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary">
                Pair selected ({selectedPairableRows.length})
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Space>

      <Divider />

      {dataSource.length > 0 && (
        <Typography.Text
          type="secondary"
          style={{ marginBottom: SPACING.sm, display: 'block' }}
        >
          {dataSource.length} candidates · {pairableCount} pairable
          {pairedCount > 0 && ` · ${pairedCount} paired this session`}
        </Typography.Text>
      )}

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={reportInProcess}
        emptyText="No products to pair"
        onChange={(_pagination, _filters, _sorter, extra): void => {
          setFilteredDataSource(extra.currentDataSource as DataType[]);
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys: Key[]): void => {
            setSelectedRowKeys(keys);
          },
        }}
      />
    </>
  );
}

export default ReportWarehouseProductsPairingContainer;
